import React, { useEffect, useRef, useState } from 'react'
import {
  Check,
  ChevronDown,
  ChevronUp,
  FileText,
  MoreVertical,
  Pencil,
  Plus,
  RefreshCcw,
  Trash2,
  X,
} from 'lucide-react'
import {
  AddFileBtn,
  ChecklistRowBody,
  ChecklistRowDescription,
  ChecklistRowDescriptionText,
  ChecklistRowHeader,
  ChecklistRowIndex,
  ChecklistRowMeta,
  ChecklistRowMetaLabel,
  ChecklistRowMetaValue,
  ChecklistRow as ChecklistRowStyle,
  CommentAvatar,
  CommentBubble,
  CommentContent,
  CommentMeta,
  CommentsBlock,
  CommentsHeader,
  DangerButton,
  FileNonImage,
  FileThumb,
  FilesGrid,
  ItemStatusBadge,
  KebabButton,
  KebabMenu,
  KebabMenuItem,
  KebabSeparator,
  KebabWrapper,
  NewCommentInput,
  NewCommentRow,
  PrimaryButton,
  RemoveFileBtn,
  TextArea,
  VoceTag,
} from './styles'
import { ChecklistItem } from './types'
import { ApprovalApi } from './api'

interface Props {
  item: ChecklistItem
  index: number
  isAdmin: boolean
  currentUserId?: number
  locked: boolean
  onItemChanged: (item: ChecklistItem) => void
  onItemRemoved: (id: number) => void
}

const isImageMime = (mime?: string | null) =>
  !!mime && mime.toLowerCase().startsWith('image/')

const formatDateTime = (iso?: string | null) => {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const initials = (name?: string | null) => {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0].slice(0, 2)
  return (parts[0][0] ?? '') + (parts[parts.length - 1][0] ?? '')
}

const ChecklistItemRow: React.FC<Props> = ({
  item,
  index,
  isAdmin,
  currentUserId,
  locked,
  onItemChanged,
  onItemRemoved,
}) => {
  const [isOpen, setIsOpen] = useState(item.status === 'PENDING')
  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState(item.description)
  const [commentDraft, setCommentDraft] = useState('')
  const [busy, setBusy] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const readOnly = locked
  const canDecide = isAdmin && !readOnly

  useEffect(() => {
    if (!menuOpen) return
    const handleDocClick = (e: MouseEvent) => {
      if (!menuRef.current) return
      if (!menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false)
      }
    }
    document.addEventListener('mousedown', handleDocClick)
    return () => document.removeEventListener('mousedown', handleDocClick)
  }, [menuOpen])

  const handleDecide = async (status: 'APPROVED' | 'REJECTED') => {
    if (!canDecide || busy) return
    setBusy(true)
    try {
      const updated = await ApprovalApi.decideChecklistItem(item.id, status)
      onItemChanged(updated)
    } finally {
      setBusy(false)
    }
  }

  const handleResetDecision = async () => {
    if (!canDecide || busy) return
    setBusy(true)
    try {
      const updated = await ApprovalApi.decideChecklistItem(item.id, 'PENDING')
      onItemChanged(updated)
    } finally {
      setBusy(false)
    }
  }

  const handleSaveDescription = async () => {
    if (readOnly || !descriptionDraft.trim() || descriptionDraft === item.description) {
      setEditingDescription(false)
      return
    }
    setBusy(true)
    try {
      const updated = await ApprovalApi.updateChecklistItem(item.id, {
        description: descriptionDraft.trim(),
      })
      onItemChanged({ ...updated, Comments: item.Comments, Files: item.Files })
    } finally {
      setBusy(false)
      setEditingDescription(false)
    }
  }

  const handleRemove = async () => {
    if (readOnly || busy) return
    if (!window.confirm('Remover este item do checklist?')) return
    setBusy(true)
    try {
      await ApprovalApi.removeChecklistItem(item.id)
      onItemRemoved(item.id)
    } finally {
      setBusy(false)
    }
  }

  const handleAddFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file || readOnly) return
    setBusy(true)
    try {
      const created = await ApprovalApi.addChecklistFile(item.id, file)
      onItemChanged({ ...item, Files: [...item.Files, created] })
    } finally {
      setBusy(false)
    }
  }

  const handleRemoveFile = async (fileInItemId: number) => {
    if (readOnly || busy) return
    setBusy(true)
    try {
      await ApprovalApi.removeChecklistFile(fileInItemId)
      onItemChanged({
        ...item,
        Files: item.Files.filter((f) => f.id !== fileInItemId),
      })
    } finally {
      setBusy(false)
    }
  }

  const handleAddComment = async () => {
    const trimmed = commentDraft.trim()
    if (!trimmed || readOnly || busy) return
    setBusy(true)
    try {
      const created = await ApprovalApi.addChecklistComment(item.id, trimmed)
      onItemChanged({ ...item, Comments: [...item.Comments, created] })
      setCommentDraft('')
    } finally {
      setBusy(false)
    }
  }

  const handleRemoveComment = async (commentId: number) => {
    if (busy) return
    setBusy(true)
    try {
      await ApprovalApi.removeChecklistComment(commentId)
      onItemChanged({
        ...item,
        Comments: item.Comments.filter((c) => c.id !== commentId),
      })
    } finally {
      setBusy(false)
    }
  }

  const approver = item.Approver
  const approverIsCurrentUser =
    !!currentUserId && approver?.id === currentUserId

  const openEditFromMenu = () => {
    setIsOpen(true)
    setDescriptionDraft(item.description)
    setEditingDescription(true)
    setMenuOpen(false)
  }

  const removeFromMenu = () => {
    setMenuOpen(false)
    handleRemove()
  }

  return (
    <ChecklistRowStyle>
      <ChecklistRowHeader $clickable onClick={() => setIsOpen((p) => !p)}>
        <ChecklistRowIndex>{String(index + 1).padStart(2, '0')}</ChecklistRowIndex>
        <ChecklistRowDescription>
          <ChecklistRowDescriptionText>{item.description}</ChecklistRowDescriptionText>
          <ItemStatusBadge $status={item.status}>
            {item.status === 'PENDING' ? '—' : item.status === 'APPROVED' ? 'Aprovado' : 'Reprovado'}
          </ItemStatusBadge>
        </ChecklistRowDescription>
        <ChecklistRowMeta>
          <ChecklistRowMetaLabel>Aprovador</ChecklistRowMetaLabel>
          <ChecklistRowMetaValue>
            {approver?.name ?? '—'}
            {approverIsCurrentUser && <VoceTag>VOCÊ</VoceTag>}
          </ChecklistRowMetaValue>
        </ChecklistRowMeta>
        {!readOnly && (
          <KebabWrapper ref={menuRef} onClick={(e) => e.stopPropagation()}>
            <KebabButton
              type='button'
              onClick={() => setMenuOpen((p) => !p)}
              title='Mais opções'
            >
              <MoreVertical size={16} />
            </KebabButton>
            {menuOpen && (
              <KebabMenu>
                <KebabMenuItem
                  type='button'
                  onClick={() => {
                    setMenuOpen(false)
                    handleDecide('APPROVED')
                  }}
                  disabled={!canDecide || busy || item.status === 'APPROVED'}
                >
                  <Check size={14} /> Aprovar este item
                </KebabMenuItem>
                <KebabMenuItem
                  type='button'
                  onClick={() => {
                    setMenuOpen(false)
                    handleDecide('REJECTED')
                  }}
                  disabled={!canDecide || busy || item.status === 'REJECTED'}
                >
                  <X size={14} /> Reprovar este item
                </KebabMenuItem>
                <KebabMenuItem
                  type='button'
                  onClick={() => {
                    setMenuOpen(false)
                    handleResetDecision()
                  }}
                  disabled={!canDecide || busy || item.status === 'PENDING'}
                >
                  <RefreshCcw size={14} /> Voltar para pendente
                </KebabMenuItem>
                <KebabSeparator />
                <KebabMenuItem
                  type='button'
                  onClick={openEditFromMenu}
                  disabled={busy}
                >
                  <Pencil size={14} /> Editar descrição
                </KebabMenuItem>
                <KebabMenuItem
                  type='button'
                  $danger
                  onClick={removeFromMenu}
                  disabled={busy}
                >
                  <Trash2 size={14} /> Remover item
                </KebabMenuItem>
              </KebabMenu>
            )}
          </KebabWrapper>
        )}
        {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </ChecklistRowHeader>

      {isOpen && (
        <ChecklistRowBody>
          {editingDescription && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <TextArea
                value={descriptionDraft}
                onChange={(e) => setDescriptionDraft(e.target.value)}
                disabled={readOnly}
              />
              <div style={{ display: 'flex', gap: 8 }}>
                <PrimaryButton type='button' onClick={handleSaveDescription} disabled={busy}>
                  Salvar
                </PrimaryButton>
                <DangerButton
                  type='button'
                  onClick={() => {
                    setDescriptionDraft(item.description)
                    setEditingDescription(false)
                  }}
                >
                  Cancelar
                </DangerButton>
              </div>
            </div>
          )}

          <FilesGrid>
            {item.Files.map((f) => {
              const file = f.GeneralFile
              if (isImageMime(file.mimeType)) {
                return (
                  <FileThumb key={f.id}>
                    <a href={file.fileUrl} target='_blank' rel='noreferrer'>
                      <img src={file.fileUrl} alt={file.title ?? 'anexo'} />
                    </a>
                    {!readOnly && (
                      <RemoveFileBtn
                        type='button'
                        onClick={() => handleRemoveFile(f.id)}
                        title='Remover anexo'
                      >
                        <X size={10} />
                      </RemoveFileBtn>
                    )}
                  </FileThumb>
                )
              }
              return (
                <FileNonImage key={f.id} title={file.originalName ?? file.title ?? ''}>
                  <FileText size={20} />
                  <span>{(file.originalName ?? file.title ?? 'arquivo').slice(0, 20)}</span>
                  {!readOnly && (
                    <RemoveFileBtn
                      type='button'
                      onClick={() => handleRemoveFile(f.id)}
                      title='Remover anexo'
                    >
                      <X size={10} />
                    </RemoveFileBtn>
                  )}
                </FileNonImage>
              )
            })}
            {!readOnly && (
              <AddFileBtn
                onClick={() => fileInputRef.current?.click()}
                title='Adicionar anexo'
              >
                <Plus size={22} />
              </AddFileBtn>
            )}
            <input
              ref={fileInputRef}
              type='file'
              style={{ display: 'none' }}
              onChange={handleAddFile}
            />
          </FilesGrid>

          <CommentsBlock>
            <CommentsHeader>Comentários</CommentsHeader>
            {item.Comments.map((c) => (
              <CommentBubble key={c.id}>
                <CommentAvatar>{initials(c.Author?.name)}</CommentAvatar>
                <CommentContent>
                  <CommentMeta>
                    <strong>{c.Author?.name ?? 'Usuário'}</strong>
                    <span>{formatDateTime(c.createdAt)}</span>
                    {!readOnly && c.authorId === currentUserId && (
                      <button
                        type='button'
                        onClick={() => handleRemoveComment(c.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#9ca3af',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                        title='Remover comentário'
                      >
                        <Trash2 size={12} />
                      </button>
                    )}
                  </CommentMeta>
                  <span>{c.comment}</span>
                </CommentContent>
              </CommentBubble>
            ))}
            {!readOnly && (
              <NewCommentRow>
                <NewCommentInput
                  placeholder='Escreva um comentário...'
                  value={commentDraft}
                  onChange={(e) => setCommentDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddComment()
                    }
                  }}
                  disabled={busy}
                />
                <PrimaryButton
                  type='button'
                  onClick={handleAddComment}
                  disabled={busy || !commentDraft.trim()}
                >
                  Enviar
                </PrimaryButton>
              </NewCommentRow>
            )}
          </CommentsBlock>
        </ChecklistRowBody>
      )}
    </ChecklistRowStyle>
  )
}

export default ChecklistItemRow
